import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { SITE, BRAND } from '@/lib/site';
import { trackEvent, EVENTS } from '@/lib/analytics';

const COLS = [
  {
    title: 'Contractor Growth',
    links: [
      { label: 'Grow Your Business', to: '/contractors/grow-your-business' },
      { label: 'Get Qualified Leads', to: '/contractors/get-qualified-leads' },
      { label: 'Renovation Leads', to: '/contractors/renovation-leads' },
      { label: 'Renovation Appointments', to: '/contractors/renovation-appointments' },
    ],
  },
  {
    title: 'Services',
    links: [
      { label: 'Web Design & Development', to: '/services/web-design-development' },
      { label: 'PHP Migration & Modernization', to: '/php-migration' },
      { label: 'SEO & Local SEO', to: '/services/seo-local-seo' },
      { label: 'Google Ads', to: '/services/google-ads' },
      { label: 'Meta Ads', to: '/services/meta-ads' },
      { label: 'Social Media Marketing', to: '/services/social-media-marketing' },
      { label: 'Lead Generation', to: '/services/lead-generation' },
      { label: 'Conversion Tracking & Analytics', to: '/services/tracking-analytics' },
    ],
  },
  {
    title: 'Homeowners',
    links: [
      { label: 'Get Renovation Quotes', to: '/get-renovation-quotes' },
      { label: 'Renovation Services', to: '/renovations' },
      { label: 'Planning Tools', to: '/tools' },
      { label: 'Cost Estimator', to: '/renovation-cost-estimator' },
      { label: 'How It Works', to: '/how-it-works' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Renovation Resources', to: '/resources' },
      { label: 'Case Studies', to: '/case-studies' },
      { label: 'Service Areas', to: '/locations' },
      { label: 'Sitemap', to: '/sitemap' },
      { label: 'Contact', to: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', to: '/privacy-policy' },
      { label: 'Terms', to: '/terms' },
      { label: 'Accessibility', to: '/accessibility' },
    ],
  },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-ink text-silver hairline-top">
      <div className="container-site pt-16 sm:pt-20 pb-28 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-brand-logo" aria-label="HyenaMind home">
              <picture>
                <source srcSet={BRAND.emblemSmallWebp} type="image/webp" />
                <img src={BRAND.emblemSmall} alt="HyenaMind logo" className="h-11 w-auto" width="46" height="55" />
              </picture>
              <span className="font-display text-2xl font-semibold tracking-tight text-bone">
                Hyena<span className="text-steel-light">Mind</span>
              </span>
            </Link>
            <p className="mt-2 text-xs font-bold uppercase tracking-[0.22em] text-steel-light">{SITE.tagline}</p>
            <p className="mt-5 max-w-sm text-[15px] leading-relaxed text-silver/80">{SITE.description}</p>
            <div className="mt-7 flex flex-col gap-3 text-[15px]">
              <a
                href={SITE.phoneHref}
                onClick={() => trackEvent(EVENTS.phoneClick, { location: 'footer' })}
                data-testid="footer-phone-link"
                className="flex items-center gap-3 font-bold text-bone hover:text-steel-light transition-colors"
              >
                <Phone className="h-4 w-4 text-steel-light" /> {SITE.phone}
              </a>
              <a
                href={`mailto:${SITE.email}`}
                onClick={() => trackEvent(EVENTS.emailClick, { location: 'footer' })}
                data-testid="footer-email-link"
                className="flex items-center gap-3 hover:text-bone transition-colors"
              >
                <Mail className="h-4 w-4 text-steel-light" /> {SITE.email}
              </a>
              <span className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-steel-light shrink-0" /> {SITE.address}
              </span>
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
            {COLS.map((col) => (
              <div key={col.title}>
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-bone/60 mb-4">{col.title}</h3>
                <ul className="flex flex-col gap-3">
                  {col.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link to={l.to} className="text-[15px] text-silver/80 hover:text-bone transition-colors">
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-14 pt-7 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-xs text-silver/60">© {new Date().getFullYear()} HyenaMind. All rights reserved.</p>
          <Link
            to="/contact"
            data-testid="footer-contact-cta"
            className="group inline-flex items-center gap-2 text-sm font-bold text-bone hover:text-steel-light transition-colors"
          >
            Start a conversation
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </footer>
  );
}
